/**
 * Client-side vault cryptography — AES-GCM 256 with PBKDF2 key derivation.
 *
 * Design goals (Option B — opt-in coffre-fort):
 *   • End-to-end for OPTED-IN items only.
 *   • Server never sees the passphrase nor the derived key.
 *   • Server stores base64 ciphertext + IV + salt + verifier only.
 *   • Losing the passphrase = losing access to vaulted items (documented).
 *
 * Data flow:
 *   1. Setup: user picks passphrase → we generate a random 16-byte salt,
 *      derive a CryptoKey via PBKDF2 (250k iterations), then encrypt the
 *      known plaintext "TOFBOX-VAULT-V1|<user_id>" (the "verifier"). Salt
 *      + IV + verifier ciphertext are POSTed to the server.
 *   2. Unlock (any session): server returns salt + verifier + IV. Client
 *      re-derives the key from the entered passphrase and tries to
 *      decrypt the verifier. If the plaintext matches, the passphrase
 *      is correct → key kept in memory for the session.
 *   3. Encrypt item: JSON.stringify sensitive fields → encrypt with the
 *      unlocked key (fresh IV per record) → send ciphertext to server.
 *   4. Decrypt item: server sends ciphertext + IV, client decrypts using
 *      the unlocked in-memory key.
 *
 * We deliberately use browser-native WebCrypto (no external deps). It is
 * available on all supported PWA targets (Chromium, WebKit, Gecko).
 */

const PBKDF2_ITER = 250_000;   // OWASP 2023+ minimum for PBKDF2-SHA-256
const KEY_LEN_BITS = 256;
const IV_BYTES = 12;           // 96-bit IV required for AES-GCM
const SALT_BYTES = 16;

const enc = new TextEncoder();
const dec = new TextDecoder();

// ─────────────────────────── base64 helpers ─────────────────────────────

/** Uint8Array → base64 (URL-safe not required — we transport via JSON). */
export function bytesToBase64(bytes) {
  let bin = '';
  for (let i = 0; i < bytes.byteLength; i += 1) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

/** base64 → Uint8Array. */
export function base64ToBytes(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

// ─────────────────────────── core primitives ─────────────────────────────

function randomBytes(n) {
  const buf = new Uint8Array(n);
  window.crypto.getRandomValues(buf);
  return buf;
}

/** Derive a non-exportable AES-GCM CryptoKey from a passphrase + salt. */
export async function deriveKey(passphrase, saltBytes) {
  const rawKey = await window.crypto.subtle.importKey(
    'raw',
    enc.encode(passphrase),
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  );
  return window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBytes,
      iterations: PBKDF2_ITER,
      hash: 'SHA-256',
    },
    rawKey,
    { name: 'AES-GCM', length: KEY_LEN_BITS },
    false, // non-exportable — even the JS heap dump won't yield the raw key
    ['encrypt', 'decrypt'],
  );
}

async function encryptBytes(key, plaintext) {
  const iv = randomBytes(IV_BYTES);
  const ct = new Uint8Array(
    await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext),
  );
  return { iv, ct };
}

async function decryptBytes(key, iv, ct) {
  return new Uint8Array(
    await window.crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct),
  );
}

// ─────────────────────────── public API ─────────────────────────────

const VERIFIER_TEMPLATE = 'TOFBOX-VAULT-V1|';

/** First-time vault setup. Returns the payload to POST to the server. */
export async function buildSetupPayload(passphrase, userId) {
  if (!passphrase || passphrase.length < 8) {
    throw new Error('Le mot de passe du coffre doit contenir au moins 8 caractères.');
  }
  const salt = randomBytes(SALT_BYTES);
  const key = await deriveKey(passphrase, salt);
  const verifierPlain = enc.encode(VERIFIER_TEMPLATE + userId);
  const { iv, ct } = await encryptBytes(key, verifierPlain);
  return {
    salt_b64: bytesToBase64(salt),
    verifier_iv_b64: bytesToBase64(iv),
    verifier_ct_b64: bytesToBase64(ct),
    // The freshly-derived key is returned so the UI can immediately treat
    // the vault as "unlocked" for the current session without asking again.
    key,
  };
}

/** Attempt to unlock the vault with a passphrase.
 *  Returns { ok: true, key } on success, { ok: false } otherwise. */
export async function tryUnlock({ passphrase, salt_b64, verifier_iv_b64, verifier_ct_b64, userId }) {
  try {
    const salt = base64ToBytes(salt_b64);
    const iv = base64ToBytes(verifier_iv_b64);
    const ct = base64ToBytes(verifier_ct_b64);
    const key = await deriveKey(passphrase, salt);
    const plain = await decryptBytes(key, iv, ct);
    const text = dec.decode(plain);
    // Guard against a false-positive: PBKDF2 will succeed on any input,
    // but AES-GCM auth tag WILL fail if the passphrase is wrong. Belt +
    // suspenders: verify the plaintext template too.
    if (text.startsWith(VERIFIER_TEMPLATE) && text.endsWith(userId)) {
      return { ok: true, key };
    }
    return { ok: false };
  } catch {
    return { ok: false };
  }
}

/** Encrypt a JSON-serialisable object with the unlocked vault key. */
export async function encryptJson(key, obj) {
  const { iv, ct } = await encryptBytes(key, enc.encode(JSON.stringify(obj)));
  return {
    iv_b64: bytesToBase64(iv),
    ct_b64: bytesToBase64(ct),
  };
}

/** Decrypt a ciphertext previously produced by `encryptJson`. */
export async function decryptJson(key, iv_b64, ct_b64) {
  const iv = base64ToBytes(iv_b64);
  const ct = base64ToBytes(ct_b64);
  const plain = await decryptBytes(key, iv, ct);
  return JSON.parse(dec.decode(plain));
}


// ─────────────────────────── Recovery key ─────────────────────────────
// A recovery key is a printable 24-character random string (base32-ish
// alphabet excluding easy-to-confuse chars). It is derived (PBKDF2) into
// an AES key that wraps the vault passphrase.
const RECOVERY_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no I O 0 1
const RECOVERY_LENGTH = 24; // 24 × 5 bits = 120 bits of entropy

/** Generate a fresh printable recovery key (24 chars, groups of 4). */
export function generateRecoveryKey() {
  const bytes = randomBytes(RECOVERY_LENGTH);
  let out = '';
  for (let i = 0; i < RECOVERY_LENGTH; i += 1) {
    out += RECOVERY_ALPHABET[bytes[i] % RECOVERY_ALPHABET.length];
    if ((i + 1) % 4 === 0 && i + 1 < RECOVERY_LENGTH) out += '-';
  }
  return out;
}

/** Normalise a user-typed recovery key: uppercase, strip spaces/dashes. */
export function normaliseRecoveryKey(input) {
  return String(input || '').toUpperCase().replace(/[^A-Z2-9]/g, '');
}

/** Encrypt the vault passphrase with a fresh recovery-derived key.
 *  Returns { salt_b64, iv_b64, ct_b64 } to POST to /vault/recovery/enable. */
export async function wrapPassphraseWithRecoveryKey(passphrase, recoveryKey) {
  const salt = randomBytes(SALT_BYTES);
  const key = await deriveKey(normaliseRecoveryKey(recoveryKey), salt);
  const { iv, ct } = await encryptBytes(key, enc.encode(passphrase));
  return {
    salt_b64: bytesToBase64(salt),
    iv_b64: bytesToBase64(iv),
    ct_b64: bytesToBase64(ct),
  };
}

/** Reverse of `wrapPassphraseWithRecoveryKey`. Returns the original
 *  passphrase (string) on success, throws on failure (wrong key). */
export async function unwrapPassphraseWithRecoveryKey({ recoveryKey, salt_b64, iv_b64, ct_b64 }) {
  const salt = base64ToBytes(salt_b64);
  const iv = base64ToBytes(iv_b64);
  const ct = base64ToBytes(ct_b64);
  const key = await deriveKey(normaliseRecoveryKey(recoveryKey), salt);
  const plain = await decryptBytes(key, iv, ct);
  return dec.decode(plain);
}


// ─────────────────────────── Cross-device transfer ─────────────────────────
// The transfer flow reuses the PBKDF2 → AES-GCM primitive but with a
// randomly-generated 128-bit secret in place of the user's passphrase.
// The secret travels via QR (out-of-band), never touching the server.

/** Random 128-bit URL-safe base64 secret — small enough to fit comfortably
 *  in a QR code alongside a session id (~50 chars total). */
export function generateTransferSecret() {
  const bytes = randomBytes(16); // 128 bits
  return bytesToBase64(bytes).replace(/=+$/, '');
}

/** Encrypt the vault passphrase with a fresh transfer secret. Returns the
 *  payload to POST to /api/vault/transfer/create. */
export async function wrapPassphraseForTransfer(passphrase, secretB64) {
  const salt = randomBytes(SALT_BYTES);
  const key = await deriveKey(secretB64, salt);
  const { iv, ct } = await encryptBytes(key, enc.encode(passphrase));
  return {
    salt_b64: bytesToBase64(salt),
    iv_b64: bytesToBase64(iv),
    ct_b64: bytesToBase64(ct),
  };
}

/** Reverse of `wrapPassphraseForTransfer`. Returns the passphrase string
 *  on success, throws on tampering / wrong secret. */
export async function unwrapPassphraseFromTransfer({ secretB64, salt_b64, iv_b64, ct_b64 }) {
  const salt = base64ToBytes(salt_b64);
  const iv = base64ToBytes(iv_b64);
  const ct = base64ToBytes(ct_b64);
  const key = await deriveKey(secretB64, salt);
  const plain = await decryptBytes(key, iv, ct);
  return dec.decode(plain);
}
